import {
  ADMIN_ORDER_DETAILS,
  AdminInput,
  ArrowRed,
  Box,
  Button,
  Disclaimer,
  Div,
  formatDateTimeShort,
  formatter,
  Icon,
  ListSpinner,
  lightTheme,
  ModalPortal,
  P,
  Radio,
  renderProviderName,
  SupportTicket,
  useDebouncedValue,
  useSafeWindowOpen,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { useTicketOrderCandidates } from '../hooks/useTicketOrderCandidates';

type Props = {
  isOpen: boolean;
  ticket: SupportTicket | null;
  busy: boolean;
  onClose: () => void;
  onAttach: (orderId: number) => Promise<boolean>;
};

/**
 * Picking the order a ticket is about, when the merchant did not.
 *
 * Every row here is an order of the ticket's own merchant - the server scopes
 * the list and refuses an attach outside it - so the danger is no longer
 * attaching a stranger's order, only the wrong one of theirs. That is what the
 * brand, the location and the time on each row are for.
 */
export const AttachOrderModal = ({
  isOpen,
  ticket,
  busy,
  onClose,
  onAttach,
}: Props) => {
  const [input, setInput] = useState('');
  const query = useDebouncedValue(input, 400);
  /* Picked, not yet attached. Two steps rather than one, because attaching
     writes into the merchant's thread and closing the modal cannot undo it. */
  const [picked, setPicked] = useState<number | null>(null);
  const openInNewTab = useSafeWindowOpen();

  useEffect(() => {
    if (!isOpen) {
      setInput('');
      setPicked(null);
    }
  }, [isOpen]);

  const { orders, isLoading, failed } = useTicketOrderCandidates({
    ticketId: ticket?.id ?? null,
    query,
    enabled: isOpen,
  });

  const attach = async () => {
    if (picked === null) return;
    const ok = await onAttach(picked);
    if (ok) onClose();
  };

  return (
    <ModalPortal
      isOpen={isOpen}
      closeModal={onClose}
      logo={false}
      header="Attach an order"
      zIndex={160}
      footer={
        <Button
          $w="100%"
          $wSM="100%"
          $selected
          disabled={picked === null || busy}
          $loading={busy}
          onClick={attach}
        >
          Assign
        </Button>
      }
      $numOfFloatingButtons={1}
    >
      {/* AdminInput rather than the header's SearchInput: that one collapses to
          an icon until it is clicked, which inside a modal reads as a missing
          field. */}
      <AdminInput
        search
        value={input}
        placeholder="Order number, customer or ID"
        onChange={(event) => setInput(event.target.value)}
      />

      {/* No scroller of its own: the modal already scrolls, and a box inside it
          clips the cards' shadows along its edges. */}
      <Div $mt="16">
        {/* The flex row is the Box itself, not a child of it: a child only
            fills its own content height, so it would sit at the top of the
            Box's 70px minimum rather than centred in it. */}
        {orders.map((order) => (
          <Box
            key={order.orderId}
            $mb="8"
            $flex
            $between
            $alignCenter
            $gap="16px"
            $pointer={!busy}
            $opacity={busy ? '0.6' : '1'}
            onClick={busy ? undefined : () => setPicked(order.orderId)}
          >
            <Div style={{ minWidth: 0 }}>
              {/* A peek at the order before committing to it, in a new tab so
                  the picker stays where it is. Stops the row's click, and is
                  shrink-wrapped so the underline and that click stay on the
                  number rather than spanning the row. */}
              <P
                $m="0"
                $width="fit-content"
                $hoverUnderline
                $pointer
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                onClick={(event) => {
                  event.stopPropagation();
                  openInNewTab(
                    ADMIN_ORDER_DETAILS.replace(':id', String(order.orderId)),
                    { immediate: true }
                  );
                }}
              >
                #{order.orderId}
                {/* The same drill-in arrow the dashboard's legend rows use.
                    A span, not Icon's own div: it lives inside a <p>. */}
                <Icon
                  as="span"
                  $size="12"
                  color={lightTheme.lightGray}
                  style={{ flex: 'none' }}
                >
                  <ArrowRed />
                </Icon>
              </P>
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                {[
                  order.customerName,
                  order.brandName,
                  /* Often the same word twice: a single-brand merchant names
                       the location after the brand. */
                  order.locationName === order.brandName
                    ? null
                    : order.locationName,
                  renderProviderName(order.provider),
                  formatDateTimeShort(order.placedAt),
                  // Dollars, like every amount the triage routes return, and
                  // unlike the cents the orders list deals in.
                  order.total === null ? null : formatter.format(order.total),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Disclaimer>
            </Div>

            <Radio checked={picked === order.orderId} disabled={busy} />
          </Box>
        ))}

        {/* A failed request must not read as an empty result - the two lead
            somewhere different, and only one of them is the agent's problem. */}
        {!isLoading && orders.length === 0 && (
          <Disclaimer $mt="24" $center $color={lightTheme.lightGray}>
            {failed
              ? "Couldn't load this merchant's orders. Try again in a moment."
              : 'No orders in the last 30 days match that.'}
          </Disclaimer>
        )}

        <ListSpinner
          loading={isLoading}
          smallest
          height="48px"
          heightSM="48px"
          mb="0"
        />
      </Div>
    </ModalPortal>
  );
};
